import type { Ref } from "vue"
import { t } from "@/app/composables/useI18n"
import { userTransactions } from "@/user-account/api"
import { ref } from "vue"
import { fetchWallets } from "../api"

interface Transaction {
  id: number
  type: string
  status: string
  amount: number
  fee?: number
  desc?: string
  message?: string
  crypto_tx_id?: string
  created_at: string
  wallet?: {
    currency?: {
      ticker: string
    }
  }
}

interface Wallet {
  id: number | null
  currency?: {
    ticker: string
  }
  type?: string
}

interface Status {
  value: string | null
  title: string
}

interface Type {
  value: string | null
  title: string
}

export function useTransactions() {
  const loading = ref(false)
  const showMoreButton = ref(true)
  const transactions: Ref<Transaction[]> = ref([])
  const currentPage = ref(1)
  const wallets: Ref<Wallet[]> = ref([])

  const isWalletOpen = ref(false)
  const isStatusOpen = ref(false)
  const isTypeOpen = ref(false)

  const selectedStatus = ref<Status>({
    value: null,
    title: t("finance.transactions_all"),
  })

  const selectedWallet = ref<Wallet>({
    id: null,
  })

  const selectedType = ref<Type>({
    value: null,
    title: t("finance.transactions_all"),
  })

  const statuses = ref<Status[]>([
    { value: null, title: t("finance.transactions_all") },
    { value: "initiated", title: "Initiated" },
    { value: "success", title: "Success" },
    { value: "cancelled", title: "Cancelled" },
    { value: "error", title: "Error" },
  ])

  const types = ref<Type[]>([
    { value: null, title: t("finance.transactions_all") },
    { value: "deposit", title: "Deposit" },
    { value: "withdrawal", title: "Withdrawal" },
    { value: "adjustment", title: "Adjustment" },
  ])

  const allWalletOption: Wallet = {
    id: null,
  }

  function closeAllFilters() {
    isWalletOpen.value = false
    isStatusOpen.value = false
    isTypeOpen.value = false
  }

  function openWalletFilter() {
    isStatusOpen.value = false
    isTypeOpen.value = false
    isWalletOpen.value = !isWalletOpen.value
  }

  function openStatusFilter() {
    isWalletOpen.value = false
    isTypeOpen.value = false
    isStatusOpen.value = !isStatusOpen.value
  }

  function openTypeFilter() {
    isWalletOpen.value = false
    isStatusOpen.value = false
    isTypeOpen.value = !isTypeOpen.value
  }

  function selectWallet(wallet: Wallet) {
    selectedWallet.value = wallet
    currentPage.value = 1
    transactions.value = []
    isWalletOpen.value = false
    loadTransactions({
      wallet_id: selectedWallet.value.id,
      status: selectedStatus.value.value,
      type: selectedType.value.value,
    })
  }

  function selectStatus(status: Status) {
    selectedStatus.value = status
    currentPage.value = 1
    transactions.value = []
    isStatusOpen.value = false
    loadTransactions({
      wallet_id: selectedWallet.value.id,
      status: selectedStatus.value.value,
      type: selectedType.value.value,
    })
  }

  function selectType(type: Type) {
    selectedType.value = type
    currentPage.value = 1
    transactions.value = []
    isTypeOpen.value = false
    loadTransactions({
      wallet_id: selectedWallet.value.id,
      status: selectedStatus.value.value,
      type: selectedType.value.value,
    })
  }

  const PAGE_SIZE = 10

  async function loadTransactions(filters?: {
    wallet_id: number | null
    status: string | null
    type: string | null
  }) {
    try {
      loading.value = true
      const { data } = await userTransactions({
        ...filters,
        page: currentPage.value,
        per_page: PAGE_SIZE,
      })

      if (data.data.length > 0) {
        if (currentPage.value === 1) {
          transactions.value = data.data
        }
        else {
          transactions.value.push(...data.data)
        }
        showMoreButton.value = data.data.length === PAGE_SIZE && data.meta.current_page < data.meta.last_page
      }
      else {
        showMoreButton.value = false
      }
    }
    catch (error) {
      console.error("Error loading transactions:", error)
      showMoreButton.value = false
    }
    finally {
      loading.value = false
    }
  }

  function loadNextPage(filters?: {
    wallet_id: number | null
    status: string | null
    type: string | null
  }) {
    currentPage.value++
    loadTransactions(filters)
  }

  async function loadUserWallets() {
    try {
      const { data } = await fetchWallets()
      wallets.value = data.data || []
    }
    catch (error) {
      console.error("Error loading wallets:", error)
    }
  }

  return {
    loading,
    showMoreButton,
    transactions,
    currentPage,
    wallets,
    isWalletOpen,
    isStatusOpen,
    isTypeOpen,
    selectedStatus,
    selectedWallet,
    selectedType,
    statuses,
    types,
    allWalletOption,
    closeAllFilters,
    openWalletFilter,
    openStatusFilter,
    openTypeFilter,
    selectWallet,
    selectStatus,
    selectType,
    loadTransactions,
    loadNextPage,
    loadUserWallets,
  }
}

export type { Status, Transaction, Type, Wallet }
